import { useMemo } from "react";

import { RecordsRootStore } from "./RecordsRootStore";
import { useFrontendContainer } from "@/infrastructure/di/FrontendProvider";
import type { FrontendContainer } from "@/infrastructure/di/FrontendContainer";

const storeRegistry = new WeakMap<FrontendContainer, RecordsRootStore>();

export function createRecordsRootStore(
  container: FrontendContainer
): RecordsRootStore {
  const existing = storeRegistry.get(container);
  if (existing) {
    return existing;
  }

  const store = new RecordsRootStore({
    fetchRecordsListUseCase: container.resolveFetchRecordsListUseCase(),
    fetchRecordsPaginatedUseCase:
      container.resolveFetchRecordsPaginatedUseCase(),
    fetchAdminRecordsPaginatedUseCase:
      container.resolveFetchAdminRecordsPaginatedUseCase(),
    fetchRecordDetailsUseCase: container.resolveFetchRecordDetailsUseCase(),
    fetchRecordMarkdownUseCase: container.resolveFetchRecordMarkdownUseCase(),
    fetchRecordMetaUseCase: container.resolveFetchRecordMetaUseCase(),
    fetchRecordsMetaBatchUseCase:
      container.resolveFetchRecordsMetaBatchUseCase(),
    deleteRecordsUseCase: container.resolveDeleteRecordsUseCase(),
    archiveRecordsUseCase: container.resolveArchiveRecordsUseCase(),
    renameRecordUseCase: container.resolveRenameRecordUseCase(),
    removeFromProjectUseCase: container.resolveRemoveFromProjectUseCase(),
    assignRecordToProjectUseCase: container.resolveAssignRecordToProjectUseCase(),
    changeRecordStatusUseCase: container.resolveChangeRecordStatusUseCase(),
    toastService: container.resolveRecordsToastService(),
  });

  storeRegistry.set(container, store);
  return store;
}

/**
 * React hook для создания/резолва singleton RecordsRootStore через DI контейнер.
 */
export function useRecordsRootStore(): RecordsRootStore {
  const container = useFrontendContainer();
  const store = useMemo(() => createRecordsRootStore(container), [container]);
  return store;
}
